import { deepFrozen } from "./frozen-collections.ts";

/**
 * What every durable (debounced) write returns — `events.updateDurable`,
 * `row.updateDurable`, a writable facade's `writeDurable` (both row facades),
 * `sheets.updateDurable`, `SourceRow.writeDurable` — at once, without
 * awaiting the server. The vocabulary type of both packages: declared here,
 * and imported from `@tento/lona/sheets` (`@tento/lona` does not re-export
 * it).
 *
 * - `applied`: what the write did locally, as a tagged union a caller
 *   switches on (`Durable.Applied`):
 *   - `{ kind: "shown", value }` — the change is showing now, applied
 *     optimistically before the verb returned, and is taken back on a
 *     refusal. Event, row and sheet writes: `value` is the patched event
 *     for `events.updateDurable`, `undefined` for a write whose local change
 *     is the caller's own patch.
 *   - `{ kind: "on-settle" }` — nothing shows yet; the change is mirrored
 *     once `settled` resolves, and nothing is mirrored when it rejects.
 *     `sheets.updateDurable` of a sheet the list cache does not hold says
 *     so too (there is nothing to show; the next `list()` reads the
 *     server's value). A sheet's durable CELL write (a writable facade's `writeDurable`):
 *     the sheet's cell writer first copy-on-write promotes an ephemeral sheet
 *     and persists a lookup-seeded row, and a cell painted before those land
 *     would show a value on a row or sheet the server may never hold.
 *
 * - `settled`: resolves when the execution that carried THIS write answered
 *   without refusing it — with `S`, what the verdict decided (`void` for
 *   most writes; `SourceRow.Persisted` for a rows-side cell write, which
 *   says whether this write reached the backend or rides an ephemeral
 *   sheet's promote); rejects with the write's `SdkRequestError` subclass
 *   (`EventWriteError`, `RowWriteError`, `CellWriteError`,
 *   `SheetWriteError`, or the rows-side backend's error) once the change
 *   was taken back or re-read from the server — `outcome: "refused"` (the
 *   server said no), `"unanswered"` (no verdict; it may have been
 *   stored) or `"malformed"` (an answer that did not decode; it may have
 *   been stored too). A write whose transport gives no verdict never resolves as
 *   accepted.
 *
 * A caller that reads `settled` owns it. The client's own durable verbs
 * (`row.*Durable`, `sheets.updateDurable`, `events.updateDurable`,
 * `themes.setDurable`) watch it: the refusal of a write whose `settled`
 * nobody read goes to the client's `ClientConfig.requestErrorHandler`
 * instead of an unhandled rejection. A rows-side `SourceRow.writeDurable`
 * has no client to hand it to — its caller attaches its report. A surface
 * that does not wait but reports on its own terms attaches its report:
 *
 * ```ts
 * const { applied, settled } = row.updateDurable({ heightPx }, actor);
 * if (applied.kind === "on-settle") showPending();
 * void settled.catch((e) => reportWriteError("resize the row", e));
 * ```
 *
 * The checked twin of each verb (`update` for `updateDurable`, `write` for
 * `writeDurable`, `SourceRow.write` for `writeDurable`) awaits the
 * request instead and rejects with the same error.
 */
export type Durable<T, S = void> = {
  readonly applied: Durable.Applied<T>;
  readonly settled: Promise<S>;
};

/** Types and values that go with `Durable`: `Applied`, `shown`, `ON_SETTLE`. */
export namespace Durable {
  /** What a durable write did locally before returning (see `Durable`). */
  export type Applied<T> =
    | { readonly kind: "shown"; readonly value: T }
    | { readonly kind: "on-settle" };

  /** `applied` of a write showing now: `value` is what it shows. */
  export function shown<T>(value: T): Applied<T> {
    return { kind: "shown", value };
  }

  /** `applied` of a write mirrored once `settled` resolves. */
  export const ON_SETTLE: Applied<never> = deepFrozen({ kind: "on-settle" });
}
